import DownSvg from 'assets/svg/Down';
import useFetchAttributeNamesOnly from 'hooks/Filter/useFetchAttributeNamesOnly';
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Loader from './Loader';
import useFetchAttributeNamesAlt from 'hooks/Filter/useFetchAttributeNamesAlt';
import _ from 'lodash';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import InfiniteScroller from './InfiniteScroller';
import { useOnClickOutside } from 'hooks/general/useOnOutsideClick';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';

interface Props {
    attributeType: any;
    options: any;
    offset: any;
    loading: boolean;
    count: number;
    onCheckboxChange: Function;
    fetchDropdownData: (arg1: boolean, arg2: number, arg3: string) => any;
    setApplyFilterTempLoading: Function;
    dropdownWindowHeight?: number;
    search: string;
    disabled?: boolean;
    setSearch: Function;
}

const dropdownWrapperId = 'dropdown-v2-wrapper-id';

const MultiSelectionDropdownV2: FC<Props> = ({
    loading,
    attributeType,
    onCheckboxChange,
    options,
    count,
    offset,
    fetchDropdownData,
    setApplyFilterTempLoading,
    dropdownWindowHeight = 128,
    search,
    disabled,
    setSearch,
}) => {
    const { idForRestOfComponent } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);

    const [showPopup, setShowPopup] = useState(false);
    // const [search, setSearch] = useState('');
    const [checkedOptions, setCheckedOptions] = useState<any>({});
    const outsideClickRef = useRef<any>(null);

    const onClickOfDropdown = () => {
        setShowPopup(!showPopup);
    };

    useEffect(() => {
        if (showPopup && options && Array.isArray(options) && offset.current === 0) {
            const wrapperElem = document
                .getElementById(dropdownWrapperId)
                ?.getElementsByClassName('infinite-scroll-component')[0];
            if (wrapperElem) {
                wrapperElem.scrollTo(0, 0);
            }
        }
    }, [showPopup]);

    useEffect(() => {
        setCheckedOptions({});
    }, [attributeType]);

    useEffect(() => {
        // if (showPopup && idForRestOfComponent) {
        //     offsetRef.current = 0;
        //     fetchAttributeName(search);
        // }
        if (options && Array.isArray(options) && offset.current === 0 && !search) {
            const tempCheckedOptions: any = {};
            options?.map((item) => {
                if (item?.isChecked) tempCheckedOptions[item?.attributeExId] = item;
            });
            if (Object.keys(checkedOptions)?.length === 0) {
                onCheckboxChange(tempCheckedOptions);
                setCheckedOptions(tempCheckedOptions);
            }
        }
        // if (search) setCheckedOptions({});
        //if ((offset.current > 0 && options && Array.isArray(options)) || search)
        setApplyFilterTempLoading(false);
    }, [options]);

    //close expanded attribute name on clicking outside
    const clickOutsideHandler = useCallback(
        (event: any) => {
            if (showPopup) {
                setShowPopup(false);
            }
        },
        [showPopup],
    );
    useOnClickOutside(outsideClickRef, clickOutsideHandler);

    //trigger by the infinite scroll component
    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            offset.current = offset.current + globalFilterConfig?.limit;
            fetchDropdownData(loading, offset.current, search);
        }
    };

    function checkboxOnChangeHandler(isChecked: boolean, item: any) {
        //remove item from checked options if user is unchecking
        let temp = { ...checkedOptions };
        if (!isChecked) {
            delete temp[item?.attributeExId];
        } else temp = { ...checkedOptions, [item?.attributeExId]: item };
        onCheckboxChange(temp);
        setCheckedOptions(temp);
    }

    function placeholder() {
        const keys = Object.keys(checkedOptions);
        if (keys?.length === 1) {
            return keys[0];
        }
        if (keys?.length === 0) return 'Select Variable';
        return 'Multi Selection';
    }

    //memo debounce function
    const deBouncedSearch = useCallback(_.debounce(fetchDropdownData, 1000), [attributeType, idForRestOfComponent]);
    return (
        <div id={dropdownWrapperId} className="inline-block relative" ref={outsideClickRef}>
            <div className="dropdown-height px-2 rounded border border-purpleFont border-0 dropdown-width-multi-select flex items-center gap-1 justify-between">
                <input
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    type="text"
                    className="bg-white font-xs focus:outline-none placeholder-black"
                    onChange={(e) => {
                        offset.current = 0;
                        setSearch(e.target.value);
                        deBouncedSearch(loading, offset.current, e.target.value);
                    }}
                    placeholder={placeholder()}
                    value={search}
                />
                {/* <div
                    className="absolute cursor-pointer inset-0"
                    onClick={(e) => {
                        onClickOfDropdown();
                    }}
                ></div> */}
                <span
                    className="z-10 font-normal px-2 cursor-pointer text-right text-slate-300 text-base w-8 mr-[-10px]"
                    onClick={() => onClickOfDropdown()}
                >
                    <DownSvg />
                </span>
            </div>
            <div
                className={`bg-white dropdown-menu absolute z-[20] text-primary w-full shadow-l drop-shadow-lg rounded-b ${
                    showPopup ? `` : `hidden`
                }`}
            >
                <InfiniteScroller
                    data={options}
                    loading={loading}
                    hasMore={options?.length < count}
                    height={dropdownWindowHeight}
                    onNext={() => infiniteScrollOnNext(loading)}
                >
                    {loading && offset.current === 0 && <div className="absolute inset-0">{<Loader />}</div>}
                    <ul style={{ opacity: loading ? 0.3 : 1 }}>
                        {!options?.length && !loading && (
                            <div className="flex justify-center items-center h-full w-full mt-6">Not Found</div>
                        )}
                        {options?.map((item: any, index: number) => {
                            return (
                                <li key={index} className={'mx-3 mt-2'}>
                                    <div className="text-base flex gap-2 items-center" key={index}>
                                        <input
                                            disabled={disabled}
                                            type={'checkbox'}
                                            className={'checkbox-size accent-purpleFont cursor-pointer'}
                                            value={item?.attributeExId}
                                            checked={checkedOptions ? item?.attributeExId in checkedOptions : false}
                                            onChange={(e) => {
                                                checkboxOnChangeHandler(e.target.checked, item);
                                            }}
                                            //disabled={!item?.isActive}
                                        />
                                        <span style={{ wordBreak: 'break-all' }} className="font-xs">
                                            {item?.attributeExId}
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </InfiniteScroller>
            </div>
        </div>
    );
};

export default MultiSelectionDropdownV2;
